/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Button, Stack } from "@mui/material";
import { useSortableComponentList } from "cms/components/hooks/use-sortable-component-list";
import { AddLineWithIcon } from "components/add-line-with-icon";
import PositionButtons from "components/position-buttons";
import RemoveIcon from "components/remove-icon";
import { SortableContent } from "components/sortable-content";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { cloneObjectsArray } from "utils/array";
import { isArrayNotEmpty } from "utils/empty-exists";
import { ComponentGalleryItemContext } from "./component-gallery-context";

const defaultImage = Object.freeze({
    image: "",
});

/**
 * @param {object} props
 * @param {GalleryImage[]} props.imageGallery
 * @param {(images: GalleryImage[]) => void} props.onChange
 */
const ComponentGalleryEditWrapper = (props) => {
    const {
        imageGallery: initialGallery = [],
        onChange,
        defaultImpl,
        children
    } = props;
    
    const { t } = useTranslation();

    if (typeof onChange !== "function") {
        throw Error("onChange was not supplied, or is not a function");
    }

    const {
        list,
        sortableMethods,
        controls,
    } = useSortableComponentList(
        initialGallery,
        defaultImage,
        onChange
    );

    const { addNewItem, addNewItemBelow, removeItem, updateItems } = controls;

    const updateItemByPosition = (newItem, position) => {
        const imageArray = cloneObjectsArray(list);
        imageArray[position] = structuredClone(newItem);
        updateItems(imageArray);
    };
    
    return (
        <Fragment>
            <div style={{ padding: "24px 0" }}>
                <Button
                    size='medium'
                    variant='contained'
                    color={"primary"}
                    target='warning'
                    onClick={addNewItem}
                >
                    {t("commons.addImages")}
                </Button>
            </div>
            <div className='simplicity-image-gallery'>
                <Stack direction='column'>
                    {isArrayNotEmpty(list) &&
                        list.map((item, index) => {
                            const guid = item?.guid;
                            const onReload = async () => {};
                            const componentGalleryItemContext = {
                                index,
                                list,
                                guid,
                                item,
                                position: index,
                                updateItemByPosition,
                                removeItem,
                                defaultImpl,
                                onReload
                            };

                            return (
                                <ComponentGalleryItemContext.Provider
                                    key={`image-gallery-${guid}`}
                                    value={componentGalleryItemContext}
                                >
                                    <SortableContent
                                        id={guid}
                                        index={index}
                                        sortableMethods={sortableMethods}
                                    >
                                        
                                        {children}
                                        
                                        <div>
                                            <PositionButtons
                                                index={index}
                                                list={list}
                                                handleUpdate={updateItems}
                                            />
                                            <RemoveIcon
                                                className='simplicity-composer-field-editor-remove'
                                                onDeleteItem={removeItem}
                                                index={index}
                                            />
                                        </div>
                                    </SortableContent>
                                    <AddLineWithIcon
                                        addNewItem={() => addNewItemBelow(index)}
                                    />
                                </ComponentGalleryItemContext.Provider>
                            );
                        })}
                </Stack>
            </div>
        </Fragment>
    );
};
export default ComponentGalleryEditWrapper;

/**
 * @typedef {object} GalleryImage
 * @property {string} guid
 * @property {string} image
 * @property {number} position
 */
